import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, mergeProps as _mergeProps, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "d-flex justify-space-between align-center mb-6" }
const _hoisted_2 = { class: "text-body-1 text-medium-emphasis" }
const _hoisted_3 = {
  key: 0,
  class: "text-primary ml-2"
}
const _hoisted_4 = { class: "d-flex align-center gap-3" }
const _hoisted_5 = { class: "d-flex align-center" }
const _hoisted_6 = { class: "text-h4 font-weight-bold" }
const _hoisted_7 = { class: "text-caption text-success" }
const _hoisted_8 = { class: "d-flex align-center" }
const _hoisted_9 = { class: "text-h4 font-weight-bold" }
const _hoisted_10 = { class: "text-caption text-success" }
const _hoisted_11 = { class: "d-flex align-center" }
const _hoisted_12 = { class: "text-h4 font-weight-bold" }
const _hoisted_13 = { class: "text-caption text-success" }
const _hoisted_14 = { class: "d-flex align-center" }
const _hoisted_15 = { class: "text-h4 font-weight-bold" }
const _hoisted_16 = { class: "d-flex align-center" }
const _hoisted_17 = { class: "font-weight-medium" }
const _hoisted_18 = { class: "text-caption text-medium-emphasis" }
const _hoisted_19 = { class: "text-caption text-medium-emphasis" }
const _hoisted_20 = { class: "text-body-2" }
const _hoisted_21 = { class: "text-body-2" }
const _hoisted_22 = { class: "d-flex align-center" }
const _hoisted_23 = { class: "font-weight-medium" }
const _hoisted_24 = { class: "text-caption ml-1" }
const _hoisted_25 = { class: "d-flex align-center" }
const _hoisted_26 = { class: "d-flex align-center" }
const _hoisted_27 = { class: "text-center mb-3" }
const _hoisted_28 = { class: "text-h4 font-weight-bold text-info" }
const _hoisted_29 = { class: "text-caption text-medium-emphasis" }
const _hoisted_30 = {
  key: 0,
  class: "text-center py-8"
}
const _hoisted_31 = { class: "text-body-2 text-medium-emphasis mt-2" }
const _hoisted_32 = { class: "d-flex align-center" }
const _hoisted_33 = { class: "d-flex align-center" }
const _hoisted_34 = { class: "mb-4" }
const _hoisted_35 = { class: "text-body-2 text-medium-emphasis" }
const _hoisted_36 = {
  key: 0,
  class: "text-center py-12"
}
const _hoisted_37 = { class: "text-h6 text-medium-emphasis mt-4" }
const _hoisted_38 = { class: "text-body-2 text-medium-emphasis mt-2" }
const _hoisted_39 = { class: "d-flex align-center" }
const _hoisted_40 = { class: "text-h6 font-weight-bold mr-2" }
const _hoisted_41 = { class: "text-caption text-medium-emphasis" }
const _hoisted_42 = { class: "text-center" }
const _hoisted_43 = { class: "d-flex align-center mt-1" }
const _hoisted_44 = { class: "text-caption text-medium-emphasis" }

import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import OxygenChart from '@/components/charts/OxygenChart.vue'
import GrowthChart from '@/components/charts/GrowthChart.vue'
import TemperatureOxygenChart from '@/components/charts/TemperatureOxygenChart.vue'
import { useDeviceStore } from '@/views/Devices/store'
import { useMeasurementsStore } from '@/views/DeviceSamples/store'
import { useDeviceSensorManager, type DeviceSensorData } from '@/composables/useDeviceSensorManager'
import type { Device } from '@/views/Devices/models/device'
import type { DeviceSample } from '@/views/DeviceSamples/models/deviceSample'
import { useDeviceLoader } from '@/composables/useDeviceLoader'

// Stores

export default /*@__PURE__*/_defineComponent({
  __name: 'HomeView',
  setup(__props) {

const deviceStore = useDeviceStore()
const measurementsStore = useMeasurementsStore()
const sensorManager = useDeviceSensorManager()
const deviceLoader = useDeviceLoader()

// Estados reactivos
const devices = ref<Device[]>([])
const selectedDeviceId = ref<string>('')
const allMeasurements = ref<DeviceSample[]>([])
const selectedDeviceSensorData = ref<DeviceSensorData>({
  temperatureData: [],
  oxygenData: [],
  batteryData: [],
  temperatureOxygenData: [],
  lastValues: {}
})
const selectedDeviceGrowthData = ref<Array<{ date: string, growth: number, newRoots: number }>>([])

// Estados de carga
const loading = ref(false)
const error = ref<string | null>(null)

// Computed properties para estadísticas del dashboard
const dashboardStats = computed(() => {
  const totalDevices = devices.value.length
  const activeDevices = devices.value.filter(d => {
    const metadata = typeof d.metadata === 'string' && d.metadata ? JSON.parse(d.metadata) : d.metadata || {}
    return metadata.status === 'connected'
  }).length
  const offlineDevices = totalDevices - activeDevices
  const alertDevices = devices.value.filter(d => {
    const metadata = typeof d.metadata === 'string' && d.metadata ? JSON.parse(d.metadata) : d.metadata || {}
    return (metadata.batteryLevel && metadata.batteryLevel < 20) ||
      metadata.status === 'disconnected' ||
      metadata.status === 'maintenance'
  }).length

  // Calcular mediciones de hoy
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const measurementsToday = allMeasurements.value.filter(m =>
    new Date(m.createdAt).getTime() >= today.getTime()
  ).length

  // Calcular estadísticas de raíces
  const managedMeasurements = allMeasurements.value.filter(m => m.managed)
  const totalRootsDetected = managedMeasurements.reduce((sum, m) => sum + m.newRootsCount, 0)
  const totalNewRootsToday = allMeasurements.value
    .filter(m => m.managed && new Date(m.createdAt).getTime() >= today.getTime())
    .reduce((sum, m) => sum + m.newRootsCount, 0)

  // Calcular promedios
  const averageGrowthPercentage = managedMeasurements.length > 0
    ? managedMeasurements.reduce((sum, m) => sum + m.growthPercent, 0) / managedMeasurements.length
    : 0

  // Datos del dispositivo seleccionado para promedios de sensores
  const selectedDevice = devices.value.find(d => d.id === selectedDeviceId.value)
  const selectedDeviceMetadata = selectedDevice && typeof selectedDevice.metadata === 'string'
    ? JSON.parse(selectedDevice.metadata)
    : selectedDevice?.metadata || {}

  const averageHumidity = selectedDeviceSensorData.value.lastValues.oxygen ?
    Math.round(selectedDeviceSensorData.value.lastValues.oxygen * 10) / 10 : 65
  const averageOxygen = selectedDeviceSensorData.value.lastValues.oxygen ?
    Math.round(selectedDeviceSensorData.value.lastValues.oxygen * 10) / 10 : 18
  const averageBatteryLevel = selectedDeviceSensorData.value.lastValues.batteryLevel ?
    Math.round(selectedDeviceSensorData.value.lastValues.batteryLevel) : selectedDeviceMetadata?.batteryLevel || 75

  return {
    totalDevices,
    activeDevices,
    offlineDevices,
    alertDevices,
    measurementsToday,
    totalRootsDetected,
    averageGrowthPercentage: Math.round(averageGrowthPercentage * 10) / 10,
    averageHumidity,
    averageOxygen,
    averageBatteryLevel,
    totalNewRootsToday
  }
})

// Device status distribution computed
const deviceStatusCounts = computed(() => {
  return [
    { status: 'En línea', count: dashboardStats.value.activeDevices, color: '#7ED957' },
    { status: 'Advertencia', count: dashboardStats.value.alertDevices, color: '#FFA726' },
    { status: 'Desconectado', count: dashboardStats.value.offlineDevices, color: '#FF6B6B' }
  ]
})

// Fotorizotron devices data with calculated fields
const fotorizotronDevices = computed(() => {
  return devices.value.map(device => {
    // Parse metadata safely
    const metadata = typeof device.metadata === 'string' && device.metadata
      ? JSON.parse(device.metadata)
      : device.metadata || {}

    const batteryLevel = metadata?.batteryLevel || 75
    const status = metadata?.status || 'disconnected'
    const isOnline = status === 'connected'
    const isLowBattery = batteryLevel < 20

    // Calculate operating days
    const operatingDays = device.initDate ?
      Math.floor((Date.now() - new Date(device.initDate).getTime()) / (1000 * 60 * 60 * 24)) : 0

    // Get device deviceSamples for growth and root calculations
    const deviceMeasurements = allMeasurements.value.filter(m => m.deviceId === device.id)
    const managedMeasurements = deviceMeasurements.filter(m => m.managed)

    const totalRoots = managedMeasurements.reduce((sum, m) => sum + m.newRootsCount, 0)
    const todayMeasurements = managedMeasurements.filter(m => {
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      return new Date(m.createdAt).getTime() >= today.getTime()
    })
    const newRootsToday = todayMeasurements.reduce((sum, m) => sum + m.newRootsCount, 0)

    const averageGrowth = managedMeasurements.length > 0
      ? managedMeasurements.reduce((sum, m) => sum + m.growthPercent, 0) / managedMeasurements.length
      : 0

    // Get real sensor data for selected device
    let humidity = 65 // default
    let oxygen = 18 // default
    if (device.id === selectedDeviceId.value) {
      humidity = selectedDeviceSensorData.value.lastValues.oxygen ?
        Math.round(selectedDeviceSensorData.value.lastValues.oxygen * 10) / 10 : 65
      oxygen = selectedDeviceSensorData.value.lastValues.oxygen ?
        Math.round(selectedDeviceSensorData.value.lastValues.oxygen * 10) / 10 : 18
    }

    return {
      ...device,
      operatingDays,
      growth: Math.round(averageGrowth * 10) / 10,
      humidity,
      oxygen,
      totalRoots,
      newRoots: newRootsToday,
      status: isOnline ? 'online' : isLowBattery ? 'error' : 'warning',
      statusColor: isOnline ? 'success' : isLowBattery ? 'error' : 'warning',
      location: `Ubicación ${device.locationId}`, // Temporary display until we have location names
      batteryLevel,
      metadata
    }
  })
})

// Recent root detections computed
const recentRootDetections = computed(() => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  return allMeasurements.value
    .filter(m => m.managed && new Date(m.createdAt).getTime() >= today.getTime())
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 3)
    .map(m => ({
      deviceId: m.deviceId,
      deviceName: devices.value.find(d => d.id === m.deviceId)?.name || 'Dispositivo desconocido',
      newRoots: m.newRootsCount,
      growthPercentage: m.growthPercent,
      timestamp: m.createdAt.toISOString(),
      healthScore: m.rootHealthScore
    }))
})

// Notifications (keeping static for now as requested)
const notifications = ref([
  { message: 'Alerta: Batería baja en dispositivo', time: '15m', type: 'error' },
  { message: 'Medición pendiente de análisis', time: '1h', type: 'warning' },
  { message: 'Nuevas raíces detectadas', time: '2h', type: 'success' },
  { message: 'Sistema funcionando correctamente', time: '1d', type: 'info' }
])

// Sample data for charts based on selected device
const sampleSensorDataForCharts = computed(() => {
  if (!selectedDeviceSensorData.value.temperatureOxygenData.length) {
    return []
  }
  return selectedDeviceSensorData.value.temperatureOxygenData.map(data => ({
    timestamp: data.timestamp,
    humidity: 65, // Default value since we don't have humidity sensor data yet
    oxygen: data.oxygen,
    temperature: data.temperature,
    batteryLevel: selectedDeviceSensorData.value.batteryData.find(b =>
      b.timestamp === data.timestamp
    )?.batteryLevel || 75
  }))
})

const sampleGrowthDataForChart = computed(() => {
  return selectedDeviceGrowthData.value
})

// Functions to load real data
const loadDevices = async () => {
  try {
    loading.value = true

    // Usar carga condicional por rol
    await deviceLoader.loadDevicesByRole()
    devices.value = deviceStore.devices

    // Seleccionar primer device por defecto
    if (devices.value.length > 0 && !selectedDeviceId.value) {
      selectedDeviceId.value = devices.value[0].id
    }

  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Error cargando dispositivos'
    console.error('Error loading devices:', err)
  } finally {
    loading.value = false
  }
}

const loadMeasurements = async () => {
  try {
    await measurementsStore.getMeasurements()
    allMeasurements.value = measurementsStore.deviceSamples
  } catch (err) {
    console.error('Error loading deviceSamples:', err)
  }
}

const loadSelectedDeviceMeasurements = async () => {
  if (!selectedDeviceId.value) return

  try {
    loading.value = true
    const [sensorData, growthData] = await Promise.all([
      sensorManager.loadDeviceSensorData(selectedDeviceId.value),
      sensorManager.loadGrowthData(selectedDeviceId.value)
    ])

    selectedDeviceSensorData.value = sensorData
    selectedDeviceGrowthData.value = growthData
  } catch (err) {
    console.error('Error loading selected device data:', err)
  } finally {
    loading.value = false
  }
}

const loadAllData = async () => {
  await Promise.all([
    loadDevices(),
    loadMeasurements()
  ])

  if (selectedDeviceId.value) {
    await loadSelectedDeviceMeasurements()
  }
}

// Auto-refresh interval
const refreshInterval = ref<ReturnType<typeof setInterval> | null>(null)

// Watch for device selection changes
watch(selectedDeviceId, (newDeviceId) => {
  if (newDeviceId) {
    loadSelectedDeviceMeasurements()
  }
})

// Setup auto-refresh every 60 seconds
const setupAutoRefresh = () => {
  if (refreshInterval.value) {
    clearInterval(refreshInterval.value)
  }

  refreshInterval.value = setInterval(async () => {
    console.log('Auto-refreshing data...')
    await loadAllData()
  }, 60000) // 60 seconds
}

// Clear auto-refresh
const clearAutoRefresh = () => {
  if (refreshInterval.value) {
    clearInterval(refreshInterval.value)
    refreshInterval.value = null
  }
}

// Lifecycle
onMounted(() => {
  loadAllData()
  setupAutoRefresh()
})

onUnmounted(() => {
  clearAutoRefresh()
})

// Time period toggle
const timePeriod = ref('weekly')

// Format percentage with color
const getPercentageColor = (value: number, type = 'growth') => {
  if (type === 'growth') {
    return value >= 3 ? 'success' : value >= 2 ? 'warning' : 'error'
  } else if (type === 'battery') {
    return value >= 70 ? 'success' : value >= 40 ? 'warning' : 'error'
  }
  return 'default'
}

// Calculate health score color
const getHealthScoreColor = (score: number) => {
  return score >= 7 ? 'success' : score >= 5 ? 'warning' : 'error'
}

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_avatar = _resolveComponent("v-avatar")!
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_v_list_item = _resolveComponent("v-list-item")!
  const _component_v_select = _resolveComponent("v-select")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_progress_linear = _resolveComponent("v-progress-linear")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_table = _resolveComponent("v-table")!
  const _component_v_btn_toggle = _resolveComponent("v-btn-toggle")!
  const _component_v_list_item_title = _resolveComponent("v-list-item-title")!
  const _component_v_list = _resolveComponent("v-list")!
  const _component_v_sheet = _resolveComponent("v-sheet")!
  const _component_v_list_item_subtitle = _resolveComponent("v-list-item-subtitle")!
  const _component_v_container = _resolveComponent("v-container")!

  return (_openBlock(), _createBlock(_component_v_container, { fluid: "" }, {
    default: _withCtx(() => [
      _createVNode(_component_v_row, null, {
        default: _withCtx(() => [
          _createVNode(_component_v_col, { cols: "12" }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_1, [
                _createElementVNode("div", null, [
                  _cache[3] || (_cache[3] = _createElementVNode("h1", { class: "text-h4 font-weight-bold" }, "Dashboard Fotorizotron", -1)),
                  _createElementVNode("p", _hoisted_2, [
                    _cache[2] || (_cache[2] = _createTextVNode(" Monitoreo en tiempo real de dispositivos de análisis de raíces ")),
                    (selectedDeviceId.value)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_3, " • Mostrando: " + _toDisplayString(devices.value.find(d => d.id === selectedDeviceId.value)?.name), 1))
                      : _createCommentVNode("", true)
                  ])
                ]),
                _createElementVNode("div", _hoisted_4, [
                  _createVNode(_component_v_select, {
                    modelValue: selectedDeviceId.value,
                    "onUpdate:modelValue": [
                      _cache[0] || (_cache[0] = ($event: any) => ((selectedDeviceId).value = $event)),
                      loadSelectedDeviceMeasurements
                    ],
                    items: devices.value,
                    "item-title": "name",
                    "item-value": "id",
                    label: "Seleccionar Dispositivo",
                    density: "comfortable",
                    variant: "outlined",
                    "prepend-inner-icon": "mdi-camera-outline",
                    class: "device-selector",
                    style: {"min-width":"250px"},
                    loading: loading.value
                  }, {
                    item: _withCtx(({ props, item }) => [
                      _createVNode(_component_v_list_item, _mergeProps(props, {
                        title: item.raw.name,
                        subtitle: item.raw.description
                      }), {
                        prepend: _withCtx(() => [
                          _createVNode(_component_v_avatar, {
                            color: "primary",
                            size: "32"
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_icon, {
                                color: "white",
                                size: "16"
                              }, {
                                default: _withCtx(() => _cache[4] || (_cache[4] = [
                                  _createTextVNode("mdi-camera-outline")
                                ])),
                                _: 1
                              })
                            ]),
                            _: 1
                          })
                        ]),
                        append: _withCtx(() => [
                          _createVNode(_component_v_chip, {
                            color: ((typeof item.raw.metadata === 'string' ? JSON.parse(item.raw.metadata) : item.raw.metadata) || {}).status === 'connected' ? 'success' : 'warning',
                            size: "x-small",
                            "text-color": "white"
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(((typeof item.raw.metadata === 'string' ? JSON.parse(item.raw.metadata) : item.raw.metadata) ||
                        {}).status === 'connected' ? 'En línea' : 'Desconectado'), 1)
                            ]),
                            _: 2
                          }, 1032, ["color"])
                        ]),
                        _: 2
                      }, 1040, ["title", "subtitle"])
                    ]),
                    _: 1
                  }, 8, ["modelValue", "items", "loading"]),
                  _createVNode(_component_v_chip, {
                    color: "info",
                    size: "small",
                    "prepend-icon": "mdi-refresh"
                  }, {
                    default: _withCtx(() => _cache[5] || (_cache[5] = [
                      _createTextVNode(" Auto-actualización 60s ")
                    ])),
                    _: 1
                  })
                ])
              ])
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      _createVNode(_component_v_row, null, {
        default: _withCtx(() => [
          _createVNode(_component_v_col, {
            cols: "12",
            sm: "6",
            md: "3"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_card, {
                class: "rounded-lg",
                flat: "",
                elevation: "2"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_card_text, { class: "pb-0" }, {
                    default: _withCtx(() => [
                      _createElementVNode("div", _hoisted_5, [
                        _createVNode(_component_v_avatar, {
                          color: "#F5F3FF",
                          size: "48",
                          class: "mr-3"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_icon, {
                              color: "#8465FF",
                              size: "24"
                            }, {
                              default: _withCtx(() => _cache[6] || (_cache[6] = [
                                _createTextVNode("mdi-camera-outline")
                              ])),
                              _: 1
                            })
                          ]),
                          _: 1
                        }),
                        _createElementVNode("div", null, [
                          _cache[7] || (_cache[7] = _createElementVNode("div", { class: "text-caption text-medium-emphasis" }, "Total Dispositivos", -1)),
                          _createElementVNode("div", _hoisted_6, _toDisplayString(dashboardStats.value.totalDevices), 1),
                          _createElementVNode("div", _hoisted_7, _toDisplayString(dashboardStats.value.activeDevices) + " activos", 1)
                        ])
                      ])
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_v_card_text, null, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_progress_linear, {
                        "model-value": "100",
                        height: "6",
                        color: "#8465FF",
                        rounded: ""
                      })
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          _createVNode(_component_v_col, {
            cols: "12",
            sm: "6",
            md: "3"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_card, {
                class: "rounded-lg",
                flat: "",
                elevation: "2"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_card_text, { class: "pb-0" }, {
                    default: _withCtx(() => [
                      _createElementVNode("div", _hoisted_8, [
                        _createVNode(_component_v_avatar, {
                          color: "#EAF6FF",
                          size: "48",
                          class: "mr-3"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_icon, {
                              color: "#4CB3FF",
                              size: "24"
                            }, {
                              default: _withCtx(() => _cache[8] || (_cache[8] = [
                                _createTextVNode("mdi-wifi")
                              ])),
                              _: 1
                            })
                          ]),
                          _: 1
                        }),
                        _createElementVNode("div", null, [
                          _cache[9] || (_cache[9] = _createElementVNode("div", { class: "text-caption text-medium-emphasis" }, "Dispositivos Activos", -1)),
                          _createElementVNode("div", _hoisted_9, _toDisplayString(dashboardStats.value.activeDevices), 1),
                          _createElementVNode("div", _hoisted_10, _toDisplayString(Math.round((dashboardStats.value.activeDevices / dashboardStats.value.totalDevices) * 100)) + "% en línea", 1)
                        ])
                      ])
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_v_card_text, null, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_progress_linear, {
                        "model-value": (dashboardStats.value.activeDevices / dashboardStats.value.totalDevices) * 100,
                        height: "6",
                        color: "#4CB3FF",
                        rounded: ""
                      }, null, 8, ["model-value"])
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          _createVNode(_component_v_col, {
            cols: "12",
            sm: "6",
            md: "3"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_card, {
                class: "rounded-lg",
                flat: "",
                elevation: "2"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_card_text, { class: "pb-0" }, {
                    default: _withCtx(() => [
                      _createElementVNode("div", _hoisted_11, [
                        _createVNode(_component_v_avatar, {
                          color: "#F0FBE6",
                          size: "48",
                          class: "mr-3"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_icon, {
                              color: "#7ED957",
                              size: "24"
                            }, {
                              default: _withCtx(() => _cache[10] || (_cache[10] = [
                                _createTextVNode("mdi-sprout-outline")
                              ])),
                              _: 1
                            })
                          ]),
                          _: 1
                        }),
                        _createElementVNode("div", null, [
                          _cache[11] || (_cache[11] = _createElementVNode("div", { class: "text-caption text-medium-emphasis" }, "Raíces Detectadas", -1)),
                          _createElementVNode("div", _hoisted_12, _toDisplayString(dashboardStats.value.totalRootsDetected), 1),
                          _createElementVNode("div", _hoisted_13, "+" + _toDisplayString(dashboardStats.value.totalNewRootsToday) + " hoy", 1)
                        ])
                      ])
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_v_card_text, null, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_progress_linear, {
                        "model-value": "85",
                        height: "6",
                        color: "#7ED957",
                        rounded: ""
                      })
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          _createVNode(_component_v_col, {
            cols: "12",
            sm: "6",
            md: "3"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_card, {
                class: "rounded-lg",
                flat: "",
                elevation: "2"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_card_text, { class: "pb-0" }, {
                    default: _withCtx(() => [
                      _createElementVNode("div", _hoisted_14, [
                        _createVNode(_component_v_avatar, {
                          color: "#FFF8EA",
                          size: "48",
                          class: "mr-3"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_icon, {
                              color: "#FFA726",
                              size: "24"
                            }, {
                              default: _withCtx(() => _cache[12] || (_cache[12] = [
                                _createTextVNode("mdi-chart-line")
                              ])),
                              _: 1
                            })
                          ]),
                          _: 1
                        }),
                        _createElementVNode("div", null, [
                          _cache[13] || (_cache[13] = _createElementVNode("div", { class: "text-caption text-medium-emphasis" }, "Crecimiento Promedio", -1)),
                          _createElementVNode("div", _hoisted_15, _toDisplayString(dashboardStats.value.averageGrowthPercentage) + "%", 1),
                          _cache[14] || (_cache[14] = _createElementVNode("div", { class: "text-caption text-success" }, "↑ 0.3% vs ayer", -1))
                        ])
                      ])
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_v_card_text, null, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_progress_linear, {
                        "model-value": dashboardStats.value.averageGrowthPercentage * 20,
                        height: "6",
                        color: "#FFA726",
                        rounded: ""
                      }, null, 8, ["model-value"])
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      _createVNode(_component_v_row, { class: "mt-6" }, {
        default: _withCtx(() => [
          _createVNode(_component_v_col, {
            cols: "12",
            lg: "8"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_card, {
                flat: "",
                class: "rounded-lg",
                elevation: "2"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_card_title, { class: "d-flex justify-space-between align-center px-6 py-4" }, {
                    default: _withCtx(() => [
                      _cache[16] || (_cache[16] = _createElementVNode("div", { class: "text-h6 font-weight-bold" }, "Dispositivos Fotorizotron", -1)),
                      _createVNode(_component_v_btn, {
                        variant: "text",
                        color: "primary",
                        "prepend-icon": "mdi-eye"
                      }, {
                        default: _withCtx(() => _cache[15] || (_cache[15] = [
                          _createTextVNode("Ver todos")
                        ])),
                        _: 1
                      })
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_v_card_text, { class: "px-6" }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_table, null, {
                        default: _withCtx(() => [
                          _cache[21] || (_cache[21] = _createElementVNode("thead", null, [
                            _createElementVNode("tr", null, [
                              _createElementVNode("th", { class: "text-left" }, "Dispositivo"),
                              _createElementVNode("th", { class: "text-left" }, "Ubicación"),
                              _createElementVNode("th", { class: "text-left" }, "Crecimiento"),
                              _createElementVNode("th", { class: "text-left" }, "Sensores"),
                              _createElementVNode("th", { class: "text-left" }, "Raíces"),
                              _createElementVNode("th", { class: "text-left" }, "Estado"),
                              _createElementVNode("th", { class: "text-left" }, "Batería")
                            ])
                          ], -1)),
                          _createElementVNode("tbody", null, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(fotorizotronDevices.value, (device) => {
                              return (_openBlock(), _createElementBlock("tr", {
                                key: device.id,
                                class: "device-row"
                              }, [
                                _createElementVNode("td", null, [
                                  _createElementVNode("div", _hoisted_16, [
                                    _createVNode(_component_v_avatar, {
                                      size: "40",
                                      color: "#EAF6FF",
                                      class: "mr-3"
                                    }, {
                                      default: _withCtx(() => [
                                        _createVNode(_component_v_icon, {
                                          color: "#4CB3FF",
                                          size: "20"
                                        }, {
                                          default: _withCtx(() => _cache[17] || (_cache[17] = [
                                            _createTextVNode("mdi-camera-outline")
                                          ])),
                                          _: 1
                                        })
                                      ]),
                                      _: 1
                                    }),
                                    _createElementVNode("div", null, [
                                      _createElementVNode("div", _hoisted_17, _toDisplayString(device.name), 1),
                                      _createElementVNode("div", _hoisted_18, _toDisplayString(device.id), 1)
                                    ])
                                  ])
                                ]),
                                _createElementVNode("td", null, [
                                  _createElementVNode("div", null, _toDisplayString(device.location), 1),
                                  _createElementVNode("div", _hoisted_19, _toDisplayString(device.operatingDays) + " días activo", 1)
                                ]),
                                _createElementVNode("td", null, [
                                  _createVNode(_component_v_chip, {
                                    size: "small",
                                    color: getPercentageColor(device.growth),
                                    "text-color": "white"
                                  }, {
                                    default: _withCtx(() => [
                                      _createTextVNode(_toDisplayString(device.growth) + "% ", 1)
                                    ]),
                                    _: 2
                                  }, 1032, ["color"])
                                ]),
                                _createElementVNode("td", null, [
                                  _createElementVNode("div", _hoisted_20, "H: " + _toDisplayString(device.humidity) + "%", 1),
                                  _createElementVNode("div", _hoisted_21, "O₂: " + _toDisplayString(device.oxygen) + "%", 1)
                                ]),
                                _createElementVNode("td", null, [
                                  _createElementVNode("div", _hoisted_22, [
                                    _createVNode(_component_v_icon, {
                                      size: "16",
                                      color: "purple",
                                      class: "mr-1"
                                    }, {
                                      default: _withCtx(() => _cache[18] || (_cache[18] = [
                                        _createTextVNode("mdi-sprout")
                                      ])),
                                      _: 1
                                    }),
                                    _createElementVNode("span", _hoisted_23, _toDisplayString(device.totalRoots), 1),
                                    _createElementVNode("span", _hoisted_24, "(+" + _toDisplayString(device.newRoots) + ")", 1)
                                  ])
                                ]),
                                _createElementVNode("td", null, [
                                  _createElementVNode("div", _hoisted_25, [
                                    _createVNode(_component_v_icon, {
                                      color: device.status === 'online' ? 'success' : device.status === 'warning' ? 'warning' : 'error',
                                      size: "12",
                                      class: "mr-2"
                                    }, {
                                      default: _withCtx(() => _cache[19] || (_cache[19] = [
                                        _createTextVNode(" mdi-circle ")
                                      ])),
                                      _: 2
                                    }, 1032, ["color"]),
                                    _createVNode(_component_v_chip, {
                                      size: "small",
                                      color: device.statusColor,
                                      "text-color": "white"
                                    }, {
                                      default: _withCtx(() => [
                                        _createTextVNode(_toDisplayString(device.status === 'online' ? 'En línea' : device.status === 'warning' ? 'Advertencia' :
                          'Desconectado'), 1)
                                      ]),
                                      _: 2
                                    }, 1032, ["color"])
                                  ])
                                ]),
                                _createElementVNode("td", null, [
                                  _createElementVNode("div", _hoisted_26, [
                                    _createVNode(_component_v_icon, {
                                      color: getPercentageColor(device.batteryLevel, 'battery'),
                                      size: "16",
                                      class: "mr-1"
                                    }, {
                                      default: _withCtx(() => _cache[20] || (_cache[20] = [
                                        _createTextVNode(" mdi-battery ")
                                      ])),
                                      _: 2
                                    }, 1032, ["color"]),
                                    _createElementVNode("span", {
                                      class: _normalizeClass(getPercentageColor(device.batteryLevel, 'battery') === 'error' ? 'text-error' :
                          getPercentageColor(device.batteryLevel, 'battery') === 'warning' ? 'text-warning' : 'text-success')
                                    }, _toDisplayString(device.batteryLevel) + "% ", 3)
                                  ])
                                ])
                              ]))
                            }), 128))
                          ])
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }),
              _createVNode(_component_v_row, { class: "mt-6" }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_col, {
                    cols: "12",
                    md: "6"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_card, {
                        flat: "",
                        elevation: "2",
                        class: "rounded-lg"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_card_title, { class: "d-flex justify-space-between align-center px-4 py-3" }, {
                            default: _withCtx(() => [
                              _cache[22] || (_cache[22] = _createElementVNode("span", { class: "text-h6 font-weight-bold" }, "Oxígeno Actual", -1)),
                              (selectedDeviceId.value)
                                ? (_openBlock(), _createBlock(_component_v_chip, {
                                    key: 0,
                                    color: "info",
                                    size: "x-small",
                                    "text-color": "white"
                                  }, {
                                    default: _withCtx(() => [
                                      _createTextVNode(_toDisplayString(devices.value.find(d => d.id === selectedDeviceId.value)?.name), 1)
                                    ]),
                                    _: 1
                                  }))
                                : _createCommentVNode("", true)
                            ]),
                            _: 1
                          }),
                          _createVNode(_component_v_card_text, { class: "px-4" }, {
                            default: _withCtx(() => [
                              _createElementVNode("div", _hoisted_27, [
                                _createElementVNode("div", _hoisted_28, _toDisplayString(selectedDeviceSensorData.value.lastValues?.oxygen || dashboardStats.value.averageOxygen) + "% ", 1),
                                _createElementVNode("div", _hoisted_29, _toDisplayString(selectedDeviceId.value ? 'Dispositivo seleccionado' : 'Promedio del sistema'), 1)
                              ]),
                              _createVNode(OxygenChart, {
                                oxygen: selectedDeviceSensorData.value.lastValues?.oxygen || dashboardStats.value.averageOxygen
                              }, null, 8, ["oxygen"])
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_v_col, {
                    cols: "12",
                    md: "4"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_card, {
                        flat: "",
                        elevation: "2",
                        class: "rounded-lg"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_card_title, { class: "d-flex justify-space-between align-center px-4 py-3" }, {
                            default: _withCtx(() => [
                              _cache[23] || (_cache[23] = _createElementVNode("span", { class: "text-h6 font-weight-bold" }, "Crecimiento Semanal", -1)),
                              (selectedDeviceId.value)
                                ? (_openBlock(), _createBlock(_component_v_chip, {
                                    key: 0,
                                    color: "success",
                                    size: "x-small",
                                    "text-color": "white"
                                  }, {
                                    default: _withCtx(() => [
                                      _createTextVNode(_toDisplayString(devices.value.find(d => d.id === selectedDeviceId.value)?.name), 1)
                                    ]),
                                    _: 1
                                  }))
                                : _createCommentVNode("", true)
                            ]),
                            _: 1
                          }),
                          _createVNode(_component_v_card_text, { class: "px-4" }, {
                            default: _withCtx(() => [
                              (!selectedDeviceId.value || sampleGrowthDataForChart.value.length === 0)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_30, [
                                    _createVNode(_component_v_icon, {
                                      size: "48",
                                      color: "grey-lighten-2"
                                    }, {
                                      default: _withCtx(() => _cache[24] || (_cache[24] = [
                                        _createTextVNode("mdi-chart-line")
                                      ])),
                                      _: 1
                                    }),
                                    _createElementVNode("div", _hoisted_31, _toDisplayString(selectedDeviceId.value ? 'Sin datos de crecimiento' : 'Selecciona un dispositivo'), 1)
                                  ]))
                                : (_openBlock(), _createBlock(GrowthChart, {
                                    key: 1,
                                    "growth-data": sampleGrowthDataForChart.value
                                  }, null, 8, ["growth-data"]))
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }),
              _createVNode(_component_v_card, {
                flat: "",
                class: "rounded-lg mt-6",
                elevation: "2"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_card_title, { class: "d-flex justify-space-between align-center px-6 py-4" }, {
                    default: _withCtx(() => [
                      _createElementVNode("div", _hoisted_32, [
                        _cache[25] || (_cache[25] = _createElementVNode("span", { class: "text-h6 font-weight-bold" }, "Tendencias de Sensores", -1)),
                        (selectedDeviceId.value)
                          ? (_openBlock(), _createBlock(_component_v_chip, {
                              key: 0,
                              color: "primary",
                              size: "small",
                              class: "ml-3",
                              "text-color": "white"
                            }, {
                              default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(devices.value.find(d => d.id === selectedDeviceId.value)?.name), 1)
                              ]),
                              _: 1
                            }))
                          : _createCommentVNode("", true)
                      ]),
                      _createElementVNode("div", _hoisted_33, [
                        _createVNode(_component_v_btn_toggle, {
                          modelValue: timePeriod.value,
                          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((timePeriod).value = $event)),
                          mandatory: "",
                          density: "comfortable",
                          color: "primary",
                          class: "mr-3"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_btn, {
                              value: "daily",
                              size: "small"
                            }, {
                              default: _withCtx(() => _cache[26] || (_cache[26] = [
                                _createTextVNode("Diario")
                              ])),
                              _: 1
                            }),
                            _createVNode(_component_v_btn, {
                              value: "weekly",
                              size: "small"
                            }, {
                              default: _withCtx(() => _cache[27] || (_cache[27] = [
                                _createTextVNode("Semanal")
                              ])),
                              _: 1
                            }),
                            _createVNode(_component_v_btn, {
                              value: "monthly",
                              size: "small"
                            }, {
                              default: _withCtx(() => _cache[28] || (_cache[28] = [
                                _createTextVNode("Mensual")
                              ])),
                              _: 1
                            })
                          ]),
                          _: 1
                        }, 8, ["modelValue"])
                      ])
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_v_card_text, { class: "px-6" }, {
                    default: _withCtx(() => [
                      _createElementVNode("div", _hoisted_34, [
                        _createElementVNode("div", _hoisted_35, _toDisplayString(selectedDeviceId.value
                  ? `Evolución de sensores del dispositivo ${devices.value.find(d => d.id === selectedDeviceId.value)?.name}`
                  : 'Selecciona un dispositivo para ver las tendencias de sensores'), 1)
                      ]),
                      (!selectedDeviceId.value || sampleSensorDataForCharts.value.length === 0)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_36, [
                            _createVNode(_component_v_icon, {
                              size: "64",
                              color: "grey-lighten-2"
                            }, {
                              default: _withCtx(() => _cache[29] || (_cache[29] = [
                                _createTextVNode("mdi-chart-line-variant")
                              ])),
                              _: 1
                            }),
                            _createElementVNode("div", _hoisted_37, _toDisplayString(selectedDeviceId.value ? 'Sin datos de sensores disponibles' : 'Selecciona un dispositivo'), 1),
                            _createElementVNode("div", _hoisted_38, _toDisplayString(selectedDeviceId.value ? 'Los datos se actualizarán automáticamente cada 60 segundos' : 'Usa el selector de dispositivos arriba'), 1)
                          ]))
                        : (_openBlock(), _createBlock(TemperatureOxygenChart, {
                            key: 1,
                            "sensor-data": selectedDeviceSensorData.value.temperatureOxygenData,
                            title: "Temperatura vs Oxígeno",
                            height: "300px"
                          }, null, 8, ["sensor-data"]))
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          _createVNode(_component_v_col, {
            cols: "12",
            lg: "4"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_card, {
                flat: "",
                class: "rounded-lg",
                elevation: "2"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_card_title, { class: "d-flex justify-space-between align-center px-4 py-4" }, {
                    default: _withCtx(() => [
                      _cache[31] || (_cache[31] = _createElementVNode("div", { class: "text-h6 font-weight-bold" }, "Estado de la Red", -1)),
                      _createVNode(_component_v_btn, {
                        icon: "",
                        variant: "text",
                        size: "small"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_icon, null, {
                            default: _withCtx(() => _cache[30] || (_cache[30] = [
                              _createTextVNode("mdi-refresh")
                            ])),
                            _: 1
                          })
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_v_card_text, { class: "px-4" }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_list, null, {
                        default: _withCtx(() => [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(deviceStatusCounts.value, (status, i) => {
                            return (_openBlock(), _createBlock(_component_v_list_item, {
                              key: i,
                              class: "px-0 mb-2"
                            }, {
                              prepend: _withCtx(() => [
                                _createVNode(_component_v_avatar, {
                                  color: status.color,
                                  size: "12",
                                  class: "mr-3"
                                }, null, 8, ["color"])
                              ]),
                              append: _withCtx(() => [
                                _createElementVNode("div", _hoisted_39, [
                                  _createElementVNode("div", _hoisted_40, _toDisplayString(status.count), 1),
                                  _createElementVNode("div", _hoisted_41, _toDisplayString(Math.round((status.count / dashboardStats.value.totalDevices) * 100)) + "% ", 1)
                                ])
                              ]),
                              default: _withCtx(() => [
                                _createVNode(_component_v_list_item_title, { class: "text-body-2" }, {
                                  default: _withCtx(() => [
                                    _createTextVNode(_toDisplayString(status.status), 1)
                                  ]),
                                  _: 2
                                }, 1024)
                              ]),
                              _: 2
                            }, 1024))
                          }), 128))
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_v_sheet, {
                        height: "120",
                        class: "mt-4 chart-placeholder d-flex justify-center align-center rounded"
                      }, {
                        default: _withCtx(() => [
                          _createElementVNode("div", _hoisted_42, [
                            _createVNode(_component_v_icon, {
                              size: "32",
                              color: "grey-lighten-1"
                            }, {
                              default: _withCtx(() => _cache[32] || (_cache[32] = [
                                _createTextVNode("mdi-chart-donut")
                              ])),
                              _: 1
                            }),
                            _cache[33] || (_cache[33] = _createElementVNode("div", { class: "text-caption text-center mt-1" }, "Distribución de estados", -1))
                          ])
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }),
              _createVNode(_component_v_card, {
                flat: "",
                class: "rounded-lg mt-6",
                elevation: "2"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_card_title, { class: "d-flex justify-space-between align-center px-4 py-4" }, {
                    default: _withCtx(() => [
                      _cache[34] || (_cache[34] = _createElementVNode("div", { class: "text-h6 font-weight-bold" }, "Detecciones Recientes", -1)),
                      _createVNode(_component_v_chip, {
                        color: "success",
                        size: "small",
                        "text-color": "white"
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(dashboardStats.value.totalNewRootsToday) + " hoy", 1)
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_v_card_text, { class: "px-4" }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_list, null, {
                        default: _withCtx(() => [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(recentRootDetections.value, (detection, i) => {
                            return (_openBlock(), _createBlock(_component_v_list_item, {
                              key: i,
                              class: "px-0 mb-3"
                            }, {
                              prepend: _withCtx(() => [
                                _createVNode(_component_v_avatar, {
                                  color: "#F0FBE6",
                                  size: "36",
                                  class: "mr-3"
                                }, {
                                  default: _withCtx(() => [
                                    _createVNode(_component_v_icon, {
                                      color: "#7ED957",
                                      size: "18"
                                    }, {
                                      default: _withCtx(() => _cache[35] || (_cache[35] = [
                                        _createTextVNode("mdi-sprout")
                                      ])),
                                      _: 1
                                    })
                                  ]),
                                  _: 1
                                })
                              ]),
                              default: _withCtx(() => [
                                _createElementVNode("div", null, [
                                  _createVNode(_component_v_list_item_title, { class: "text-body-2 font-weight-medium" }, {
                                    default: _withCtx(() => [
                                      _createTextVNode(_toDisplayString(detection.deviceName), 1)
                                    ]),
                                    _: 2
                                  }, 1024),
                                  _createVNode(_component_v_list_item_subtitle, { class: "text-caption" }, {
                                    default: _withCtx(() => [
                                      _createTextVNode(" +" + _toDisplayString(detection.newRoots) + " raíces nuevas • " + _toDisplayString(detection.growthPercentage) + "% crecimiento ", 1)
                                    ]),
                                    _: 2
                                  }, 1024),
                                  _createElementVNode("div", _hoisted_43, [
                                    _createVNode(_component_v_chip, {
                                      size: "x-small",
                                      color: getHealthScoreColor(detection.healthScore),
                                      "text-color": "white",
                                      class: "mr-2"
                                    }, {
                                      default: _withCtx(() => [
                                        _createTextVNode(" Score: " + _toDisplayString(detection.healthScore), 1)
                                      ]),
                                      _: 2
                                    }, 1032, ["color"]),
                                    _createElementVNode("span", _hoisted_44, _toDisplayString(detection.timestamp.split(' ')[1]), 1)
                                  ])
                                ])
                              ]),
                              _: 2
                            }, 1024))
                          }), 128))
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_v_btn, {
                        color: "primary",
                        variant: "text",
                        block: "",
                        class: "mt-2"
                      }, {
                        default: _withCtx(() => _cache[36] || (_cache[36] = [
                          _createTextVNode("Ver historial completo")
                        ])),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }),
              _createVNode(_component_v_card, {
                flat: "",
                class: "rounded-lg mt-6",
                elevation: "2"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_card_title, { class: "d-flex justify-space-between align-center px-4 py-4" }, {
                    default: _withCtx(() => [
                      _cache[38] || (_cache[38] = _createElementVNode("div", { class: "text-h6 font-weight-bold" }, "Alertas del Sistema", -1)),
                      _createVNode(_component_v_btn, {
                        icon: "",
                        variant: "text",
                        size: "small"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_icon, null, {
                            default: _withCtx(() => _cache[37] || (_cache[37] = [
                              _createTextVNode("mdi-bell-outline")
                            ])),
                            _: 1
                          })
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_v_card_text, { class: "px-4" }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_list, null, {
                        default: _withCtx(() => [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(notifications.value, (notification, i) => {
                            return (_openBlock(), _createBlock(_component_v_list_item, {
                              key: i,
                              class: "px-0 mb-2"
                            }, {
                              prepend: _withCtx(() => [
                                _createVNode(_component_v_avatar, {
                                  color: notification.type === 'success' ? 'success' :
                    notification.type === 'warning' ? 'warning' :
                      notification.type === 'error' ? 'error' : 'info',
                                  size: "8",
                                  class: "mr-3"
                                }, null, 8, ["color"])
                              ]),
                              default: _withCtx(() => [
                                _createElementVNode("div", null, [
                                  _createVNode(_component_v_list_item_title, { class: "text-body-2" }, {
                                    default: _withCtx(() => [
                                      _createTextVNode(_toDisplayString(notification.message), 1)
                                    ]),
                                    _: 2
                                  }, 1024),
                                  _createVNode(_component_v_list_item_subtitle, { class: "text-caption text-medium-emphasis" }, {
                                    default: _withCtx(() => [
                                      _createTextVNode(" hace " + _toDisplayString(notification.time), 1)
                                    ]),
                                    _: 2
                                  }, 1024)
                                ])
                              ]),
                              _: 2
                            }, 1024))
                          }), 128))
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_v_btn, {
                        color: "primary",
                        variant: "text",
                        block: "",
                        class: "mt-4"
                      }, {
                        default: _withCtx(() => _cache[39] || (_cache[39] = [
                          _createTextVNode("Ver todas las alertas")
                        ])),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _: 1
  }))
}
}

})